import { Select, CreatableSelect } from '@acp/core-ui';
import {
    FormControl,
    FormErrorMessage,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import {
    Control,
    FieldValues,
    Path,
    useController,
} from 'react-hook-form';
import { ServiceProviderOption } from '@acp/shared';
import { addComplianceAgencyServiceProvider } from '../../service';
import { useRefreshServiceProviders } from '../../hooks/use-service-providers';
import { ConfirmAddProviderModal } from './confirm-add-provider';

function ServiceProviderSelect<TFormValues extends FieldValues>({
    control,
    name,
    options,
    isLoading,
    size,
    isDisabled = false,
}: {
    control: Control<TFormValues>;
    name: Path<TFormValues>;
    options: ServiceProviderOption[];
    isLoading?: boolean;
    size?: React.ComponentProps<typeof Select>['size'];
    isDisabled?: boolean;
}) {
    const methods = useController({
        name,
        control,
    });

    const {
        field,
        fieldState: { error, invalid },
    } = methods;

    // Store only the primitive (number) in RHF field rather than the option object
    // This matches the Zod schema expecting a numeric id
    // Only select an option if field.value is not null/undefined and matches an option's id
    const selectedOption = field.value != null
        ? options.find((o) => Number(o.value?.id) === Number(field.value?.id))
        : undefined;

    const refreshServiceProviders = useRefreshServiceProviders();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [pendingSupplierName, setPendingSupplierName] = React.useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const confirmAdd = async () => {
        if (pendingSupplierName === null) return;
        setIsSubmitting(true);
        try {
            const addedProvider = await addComplianceAgencyServiceProvider(pendingSupplierName);
            await refreshServiceProviders();
            field.onChange({ id: addedProvider.id, providedBy: addedProvider.supplier_name });
            onClose();
            setPendingSupplierName(null);
        } catch (catchError) {
            const err = catchError as Error;
            throw new Error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAdd = async (supplierName: string) => {
        setPendingSupplierName(supplierName);
        onOpen();
    };
    return (
        <>
            <FormControl isInvalid={invalid}>
                {/* Tooltip shows full label when truncated */}
                <Tooltip label={selectedOption?.label ?? null}>
                    <CreatableSelect<ServiceProviderOption>
                        options={options}
                        isLoading={isLoading}
                        value={selectedOption}
                        onChange={(option) => field.onChange(
                            option
                                ? option.value
                                : { id: null, providedBy: null }
                        )}
                        placeholder="Select service provider"
                        onCreateOption={handleAdd}
                        size={size}
                        isClearable
                        isDisabled={isDisabled}
                        formatCreateLabel={(inputValue) => `➕ Create "${inputValue}"`}
                    />
                </Tooltip>
                <FormErrorMessage>
                    {error && error?.message}
                </FormErrorMessage>
            </FormControl>
            <ConfirmAddProviderModal
                isOpen={isOpen}
                supplierName={pendingSupplierName}
                onChangeSupplierName={setPendingSupplierName}
                onConfirm={confirmAdd}
                onCancel={() => {
                    setPendingSupplierName(null);
                    onClose();
                }}
                isLoading={isSubmitting}
            />
        </>
    );
}

export { ServiceProviderSelect };
